/** The register's filters: one selection, used by the table, the drawer and the export (EXP-21).
 *
 * **The selection is computed once and shared, which is the point of the module.** A filtered table
 * beside an unfiltered export is not a cosmetic mismatch — it is a file called
 * `emitiq-inventory.csv` that does not contain what the person was looking at when they asked for
 * it, and nothing on screen or in the file says so. So `useRegister` returns `rows` and `csv()`
 * together, and `csv()` closes over the same array the table renders. There is no second path from
 * figures to a file.
 *
 * Three rules it keeps, each with a failure behind it:
 *
 * - **A filter value that is not on offer is not in force.** Selecting Scope 1 while "electricity"
 *   is the chosen category, or `?status=banana` in a pasted URL, would otherwise leave a filter
 *   nothing can match — and an empty result the reader caused is indistinguishable from missing
 *   data. `effective` drops any value the screen could not have offered.
 * - **The filters live in the URL** (`search.ts`). EXP-12 asks for return-to-list filters: a person
 *   opens a record from row 40 of a filtered list and comes back. Component state loses that on
 *   every navigation, and nothing fails — the list just silently un-filters.
 * - **Nothing here recomputes a figure.** `select` chooses rows; `csv` writes the wire's own
 *   decimal strings. Authoritative totals come from the server (`totals_by_scope`), and the one
 *   sum the screen does make is over the rows it is showing and is labelled as the filtered total.
 *
 * And one that arrived with EXP-39's large-register clause: **what is rendered and what is counted
 * are two different lists.** See `RENDER_CAP`.
 *
 * **The selection is the server's, and so is every count of it** (queue 4b). The register used to load
 * every figure of the window and select in the browser, which failed visibly past ten thousand. The
 * filters now go to `GET /figures` (`cutOfRegister`), the options come from `GET /figures/facets`,
 * and the rows are read a page at a time up to the ceiling the reader asked for (`rows`). The row
 * count and the whole-record sum are the server's for the whole selection; a file is made by reading
 * the whole selection at the moment it is asked for (`Register.readSelection`).
 */

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type { FacilityRow } from "../api/evidence";
import { readEveryFigure, useFigureFacets, useFiguresPaged } from "../api/queries";
import type { Figure, FigureCut, FigureCutRead, FigureFacets, Scope2Method, Window } from "../api/queries";
import { monthLabel } from "../lib/format";
import { scopeOfCategory } from "./scopeTable/model";
import type { FilterKey, FilterSearch } from "./search";
import { FILTER_KEYS, siteIds } from "./search";

/** The filters as the screen holds them: `""` means "all".
 *
 * One representation, not two. An earlier shape had `undefined` in the URL and `""` in the select
 * elements, and the two kinds of nothing drifted — a `<select value={undefined}>` is an
 * uncontrolled element, which React warns about and a reader experiences as a dropdown that
 * forgets.
 */
export type Filters = Record<FilterKey, string>;

export const NO_FILTERS: Filters = {
  scope: "",
  entity: "",
  site: "",
  sites: "",
  month: "",
  category: "",
  source: "",
  status: "",
  quality: "",
  owner: "",
  q: "",
};

/** The value the owner filter takes for *"nobody answers for this"*.
 *
 * A reserved string rather than an empty one, because `""` already means **all** in `Filters` and
 * two kinds of nothing in one field is how a filter comes to disagree with its own chip. It cannot
 * collide with a real value: every other owner option is a UUID.
 *
 * **It deliberately covers two of the four bases.** A figure may have no owner because the stream
 * it arrived through has nobody assigned, or because no stream matched it at all — and those have
 * completely different remedies: assign somebody, or declare the source. The *filter* collapses
 * them because "show me what nobody answers for" is the question a reader actually asks; the
 * **row** keeps `owner_basis`, because that is what decides what to do about it.
 */
export const UNOWNED = "unowned";

/** The evidence chain, as the register filters on it.
 *
 * **This is the only "status" a figure has, and that is worth stating rather than apologising for.**
 * EXP-21 asks for a status filter; a figure's *workflow* status is not a variable — `AWAITING_REVIEW`
 * is the only route into `VERIFIED` and a figure only exists once a verified record was promoted, so
 * a workflow filter over this list would have exactly one value. What differs between rows is
 * whether the chain behind the figure holds (F10's six links), which is the question somebody
 * closing a period is actually asking. Documents still in review are the evidence queue's list, not
 * this one.
 */
export const STATUSES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "complete", label: "Evidence complete" },
  { value: "broken", label: "Evidence incomplete" },
];

/** The data-quality grades a figure can carry, best first.
 *
 * **The wire's own closed set, not a client derivation.** `FigureOut.quality` is the server's
 * grade (ADR-0036: derived, never taken from the caller); this list only says which values are
 * worth offering. It is the one exception to *"offered from the data, never from a taxonomy"*
 * below, and for a reason: a grade is four ordered letters, and *"show me the grade-B figures"*
 * returning nothing is information — the Readiness screen's *Metered* number links here, and a
 * workspace with no grade A must land on an empty register that says so, not on every figure
 * because the filter was quietly dropped. A grade the server adds later is still offered, because
 * `optionsFor` unions in whatever is present.
 */
export const GRADES: readonly string[] = ["A", "B", "C", "D"];

export function parseFilters(search: FilterSearch): Filters {
  const filters = { ...NO_FILTERS };
  for (const key of FILTER_KEYS) filters[key] = search[key] ?? "";
  return filters;
}

/** Back to URL shape. `""` becomes absent, so a cleared filter leaves no trace in a shared link. */
export function toSearch(filters: Filters): FilterSearch {
  const search: FilterSearch = {};
  for (const key of FILTER_KEYS) search[key] = filters[key] === "" ? undefined : filters[key];
  return search;
}

export function isFiltered(filters: Filters): boolean {
  return FILTER_KEYS.some((key) => filters[key] !== "");
}

/** One key's new value, as a patch. Assembled rather than written as `{ [key]: value }`, which
 * types as a string index signature and would accept a key that is not a filter. */
function justOne(key: FilterKey, value: string | undefined): FilterSearch {
  const patch: FilterSearch = {};
  patch[key] = value;
  return patch;
}

/** What the screen can offer, derived from what is actually present.
 *
 * **Offered from the data, never from a taxonomy.** A list of every category the engine knows would
 * offer a reader eight choices, six of which return nothing. The narrowing is deliberate and
 * one-directional: sites inside the chosen entity, categories inside the chosen scope, source types
 * inside the chosen category — the axes a reader drills along.
 */
export interface Options {
  entities: readonly string[];
  /** `[facility_id, name]`, because a site name is not unique and an id is what filters. */
  sites: ReadonlyArray<readonly [string, string]>;
  months: readonly string[];
  categories: readonly string[];
  sources: readonly string[];
  /** Data-quality grades: the closed set above, plus any other grade actually present. */
  qualities: readonly string[];
  /** `[user_id, display_name]`, because two people may share a name and an id is what filters. */
  owners: ReadonlyArray<readonly [string, string]>;
}

/** Which legal entity each figure belongs to, by its site.
 *
 * From `/facilities` rather than from the figures: `FigureOut` carries no entity, and the site is
 * the thing that belongs to one. A workspace with a UK parent and a Californian subsidiary is the
 * case this exists for — one inventory, two filings, and a reader who needs to see one entity's
 * half of it.
 */
export function entityIndex(facilities: readonly FacilityRow[]): ReadonlyMap<string, string> {
  return new Map(facilities.map((facility) => [facility.id, facility.legal_entity_name]));
}

export function optionsFor(
  facets: { window: FigureFacets | undefined; scoped: FigureFacets | undefined },
  filters: Filters,
  entities: ReadonlyMap<string, string>,
): Options {
  //: **From the server's facets** (`GET /figures/facets`), not from the rows a page holds: a paged
  //: register cannot offer its own filters from the page it has. The window's facets give the sites,
  //: months and entities, and the scope's facets the categories and sources, so choosing Scope 2
  //: offers Scope 2's categories - the narrowing the register always had.
  const inWindow = facets.window;
  const entityNames = new Set<string>();
  const sites: Array<readonly [string, string]> = [];
  for (const site of inWindow?.sites ?? []) {
    const entity = entities.get(site.id);
    if (entity) entityNames.add(entity);
    if (!filters.entity || entity === filters.entity) sites.push([site.id, site.name]);
  }
  return {
    entities: [...entityNames].sort((a, b) => a.localeCompare(b)),
    sites: sites.sort((a, b) => a[1].localeCompare(b[1])),
    months: [...(inWindow?.months ?? [])].sort(),
    categories: [...(facets.scoped?.categories ?? [])].sort(),
    sources: [
      ...(filters.category
        ? (facets.scoped?.carriers_by_category?.[filters.category] ?? [])
        : (facets.scoped?.carriers ?? [])),
    ].sort(),
    qualities: GRADES,
    // **The people who answer for a stream** (M-08), from the server: a person who owns nothing is
    // not offered, because selecting one returns an empty register, which reads as missing data.
    owners: (inWindow?.owners ?? []).map((owner) => [owner.id, owner.name] as const),
  };
}

/** The filters actually in force: anything the screen could not have offered is dropped.
 *
 * Derived rather than written back with `setState`, and not written back to the URL either. Both
 * were tried: adjusting state during render re-renders to get there, and rewriting the URL means a
 * shared link quietly becomes a different link in the recipient's address bar. There is nothing to
 * store — the answer is a function of the filters and the data.
 *
 * `scope` and `status` are validated against **our own** closed sets, for the same reason. A pasted
 * `?status=banana` that matched no row would present a complete inventory as empty.
 */
export function effective(filters: Filters, options: Options): Filters {
  const keep = (value: string, offered: readonly string[]): string =>
    offered.includes(value) ? value : "";
  return {
    ...filters,
    scope: keep(filters.scope, ["1", "2", "3"]),
    status: keep(
      filters.status,
      STATUSES.map((status) => status.value),
    ),
    // `UNOWNED` is offered whatever the rows hold, because "nobody answers for this" is a
    // meaningful selection even when the answer is none — an empty result there is information
    // rather than a dropped filter.
    // Offered whatever the rows hold, like `UNOWNED`: an empty grade is a finding, not a dropped
    // filter. See `GRADES`.
    quality: keep(filters.quality, options.qualities),
    owner: keep(filters.owner, [UNOWNED, ...options.owners.map(([id]) => id)]),
    entity: keep(filters.entity, options.entities),
    site: keep(
      filters.site,
      options.sites.map(([id]) => id),
    ),
    // **The list, kept to the sites the window holds** - and the screen is told how many of those
    // asked for were dropped (`siteNarrowingModel.ts`), because a list that silently lost its members
    // would show a reader a part of what they asked for as if it were all of it.
    sites: siteIds(filters.sites)
      .filter((id) => options.sites.some(([offered]) => offered === id))
      .join(","),
    month: keep(filters.month, options.months),
    category: keep(filters.category, options.categories),
    source: keep(filters.source, options.sources),
  };
}

/** The window's facets as they would be for one scope, from the window's own: the categories of that
 * scope and the carriers those categories have. It is what `GET /figures/facets?scope=` would say, without
 * a read per scope - a category belongs to one scope (`scopeOfCategory`), so the narrowing is exact. */
function facetsOfScope(facets: FigureFacets, scope: string): FigureFacets {
  if (scope !== "1" && scope !== "2" && scope !== "3") return facets;
  const categories = facets.categories.filter((category) => scopeOfCategory(category) === scope);
  const byCategory = Object.fromEntries(
    categories.map((category) => [category, facets.carriers_by_category?.[category] ?? []]),
  );
  return {
    ...facets,
    categories,
    carriers: [...new Set(Object.values(byCategory).flat())].sort(),
    carriers_by_category: byCategory,
  };
}

/** Why exploring a scope or a site from the overview would clash with a filter already in force, or
 * `undefined` when it would not.
 *
 * Exploring *sets* a filter on the register, and `effective` drops any filter the screen could not
 * offer - so a chosen category from another scope, or a site outside the chosen entity, would be dropped
 * in silence and the reader would see a register they did not ask for. This names the clash instead. The
 * options it checks against are the window's own facets: it cannot know the proposed scope's categories
 * without a read per scope, so a category is checked against the scope it belongs to. */
export function exploreConflict(args: {
  key: "scope" | "site";
  selected: string;
  filters: Filters;
  facets: FigureFacets | undefined;
  entities: ReadonlyMap<string, string>;
  chips: readonly Chip[];
}): string | undefined {
  const { key, selected, filters, facets, entities, chips } = args;
  if (facets === undefined) return "Read the source figures and sites successfully before exploring.";
  const proposed = { ...filters, [key]: selected };
  const resolved = effective(
    proposed,
    optionsFor({ window: facets, scoped: facetsOfScope(facets, proposed.scope) }, proposed, entities),
  );
  const conflicts = chips
    .filter((chip) => chip.key !== key && resolved[chip.key] !== filters[chip.key])
    .map((chip) => chip.label);
  if (resolved[key] !== selected) {
    if (key === "site" && filters.entity) conflicts.push("Entity");
    else return "No source figures are available for this selection in the reporting window.";
  }
  return conflicts.length ? `Clear ${[...new Set(conflicts)].join(" and ")} filters before exploring this ${key}.` : undefined;
}

/** A site id no site has: the facility restriction when the filters intersect to nothing. An empty
 * list would mean "no restriction" to the server, which is the opposite. */
const NO_SITE = "00000000-0000-0000-0000-000000000000";

/** What the filters ask of the server - the one selection the table, the counts and the exports share.
 *
 * The entity, the site and the list of sites all narrow the same thing (which sites), so they are
 * intersected here into one list. A search is the server's: it reads site, category and carrier the way
 * the register always read them. */
export function cutOfRegister(filters: Filters, entities: ReadonlyMap<string, string>): FigureCut {
  const restrictions: Array<ReadonlySet<string>> = [];
  if (filters.entity) {
    restrictions.push(
      new Set([...entities].filter(([, name]) => name === filters.entity).map(([id]) => id)),
    );
  }
  if (filters.sites) restrictions.push(new Set(siteIds(filters.sites)));
  if (filters.site) restrictions.push(new Set([filters.site]));
  const [first, ...rest] = restrictions;
  const sites = first ? [...first].filter((id) => rest.every((other) => other.has(id))) : undefined;
  const search = filters.q.trim();
  return {
    ...(filters.scope ? { scope: filters.scope as "1" | "2" | "3" } : {}),
    ...(sites ? { facilityIds: sites.length > 0 ? sites : [NO_SITE] } : {}),
    ...(filters.month ? { months: [filters.month] } : {}),
    ...(filters.category ? { categories: [filters.category] } : {}),
    ...(filters.source ? { carriers: [filters.source] } : {}),
    ...(filters.status ? { statuses: [filters.status] } : {}),
    ...(filters.quality ? { qualities: [filters.quality] } : {}),
    ...(filters.owner ? { owner: filters.owner } : {}),
    ...(search ? { search } : {}),
  };
}

// ----------------------------------------------------------------- the large register (EXP-39)

/** How many rows the table puts in the document **before the reader asks for more**.
 *
 * EXP-39 asks that large registers work; the register rendered every selected figure, and the cost
 * is **12 DOM nodes and 2 focusable controls per row**, measured. Uncapped, 1,000 figures built
 * 12,116 nodes, 3,000 built 36,116, and 8,000 built 96,116 with 16,022 focusable controls —
 * linear, with no knee, so there is no size at which it stops getting worse. And it is paid again
 * on **every keystroke** in the search box, which re-filters and reconciles the whole list. At the
 * opening cap it is flat: 6,118 nodes whatever the workspace holds. (jsdom, so the node counts are
 * exact and the timings are not a browser's. The node count is the claim being made.)
 *
 * **What changed, and why it had to.** This was a cap and nothing else: the rows past it could not
 * be reached at all, and the screen told the reader to narrow with a filter instead. That is
 * defensible only while two things hold — that the server orders by tonnage descending, so the
 * unreachable rows are the smallest, and that the reader is told. The first is a server-side
 * `ORDER BY` with nothing on the client able to see it, so a performance change on an endpoint
 * nobody connected to this screen would have turned a cap a reader was told about into a register
 * hiding the row they came for, silently. A fact that load-bearing must not live somewhere this
 * code cannot check.
 *
 * So the cap is now an **opening ceiling the reader raises** (`ScreenSearch.rows`). The ordering is
 * still true and still what makes the opening view the useful one — the largest figures first is
 * what somebody closing a period wants — but honesty no longer rests on it, because every row is
 * reachable. `register.test.ts`'s *"reaches every row whatever order the server sent them in"* is
 * what fails if that stops being true.
 *
 * It is cited that way — the file, and the `it(...)` title in the words it is written in — because
 * this sentence used to assert a `test_…` identifier, which is the **backend's** naming convention
 * and resolved against nothing on either side. `test_named_tests_exist` now reads `frontend/src`
 * and would fail on it.
 *
 * **It is still not virtualisation, and must not be described as one.** Rows past the ceiling are
 * not rendered lazily on scroll; they are not rendered until a reader asks. Windowing would mean a
 * virtualisation library, which is a dependency decision no accepted ADR covers (rule 1) — and
 * hand-rolling one inside a `<table>` in a panel that scrolls inside itself trades a limit the
 * screen admits to for rows that do not exist for find-in-page or for a screen reader's row count,
 * which is a worse failure wearing a better word.
 *
 * Server-side paging was the other option and is not available: `GET /figures` takes
 * `period_start`, `period_end`, `scope` and `scope2_method` and returns a bare array. No `limit`,
 * no `offset`, no cursor, no count. The whole window is already in memory, which is what makes
 * raising the ceiling free — and what makes the total an exact number rather than a "500+".
 *
 * 500 rather than a larger round number: it is already more than anybody scrolls, and the point of
 * an opening cap is to be reached by a dataset rather than by a customer.
 */
export const RENDER_CAP = 500;

/** The ceiling in force: what the URL asked for, never below the opening cap.
 *
 * **Floored, not trusted.** A URL that *lowered* the ceiling would hide rows the product has no
 * reason to hide, and a reader following a link to a register showing 1 of 4,312 would be looking
 * at the same failure as `?status=banana` — a complete inventory presented as a fragment by a
 * parameter they did not choose. Raising is the reader's; lowering is nobody's.
 *
 * **And validated here at runtime, not only by `validateSearch`**, which turns out not to be the
 * enforcement point it is documented as: every screen reads with `useSearch({ strict: false })`,
 * and that returns the location's own parsed search rather than the validator's output — removing a
 * key from `validateSearch` fails that module's test and no screen's, which was checked by doing
 * it. So `asked` really can be whatever a pasted URL contained. The type says `number`; `?rows=abc`
 * delivers a string.
 *
 * That is not a tidiness point. Unguarded, `Math.max(500, "abc")` is `NaN`, `slice(0, NaN)` is
 * empty and `rows.length - NaN` is `NaN` — a register of 900 figures drawing **zero** rows under a
 * sentence reading *"Drawing 0 of 900"* and *"NaN not drawn yet"*, from a link somebody pasted. An
 * inventory that reads as empty is the one mistake nobody double-checks, so the coercion is here
 * rather than in a validator that the reader does not go through.
 */
export function ceiling(asked: number | undefined): number {
  const n = Number(asked);
  return Number.isSafeInteger(n) && n > RENDER_CAP ? n : RENDER_CAP;
}

/** How much further a single press moves the ceiling. The opening cap again, so "show more" and
 * the opening view are the same size and a reader can predict the second press from the first. */
export const RENDER_STEP = RENDER_CAP;

/** The two lists, kept apart on purpose.
 *
 * **A ceiling that changed a total would be the worst possible version of this.** The filtered
 * total, the row count in the panel's subtitle, the export and its filename are all over `rows` —
 * the whole selection — and only the table's body is over `shown`. One list would make a register
 * silently report 500 records and 4,700 tonnes of an inventory holding 4,312 records and 38,000,
 * which is a wrong figure produced by a performance change, with nothing on screen admitting it.
 * `register.test.ts`'s *"changes what is rendered and never what is counted"* is what fails — and
 * *"still never changes what is counted, at any ceiling"* is the same property at every ceiling the
 * reader can raise it to.
 */
export interface Rendered {
  /** The rows the table puts in the document: at most `cap` of them. */
  shown: readonly Figure[];
  /** How many of the selection are not in the document - not drawn, and not yet read from the server.
   * Zero for almost every workspace. */
  hidden: number;
  /** The ceiling that produced `shown`, so the screen can offer the next one. */
  cap: number;
}

export function render(
  rows: readonly Figure[],
  cap: number = RENDER_CAP,
  /** How many figures the selection holds, when more than `rows` is loaded (a paged register). */
  total: number = rows.length,
): Rendered {
  const shown = rows.length <= cap ? rows : rows.slice(0, cap);
  return { shown, hidden: Math.max(0, total - shown.length), cap };
}

/** One active filter, named so a reader can see and remove it (EXP-21's "visible context").
 *
 * A chip per filter rather than a count: "3 filters" tells somebody that something is hidden and
 * not what, which is the state in which people conclude their data is missing. The `key` is what
 * the chip's own control clears — one at a time, because clearing all of them to adjust one is how
 * a reader loses the other two.
 */
export interface Chip {
  key: FilterKey;
  /** Which dimension, in the reader's words. */
  label: string;
  /** The chosen value, rendered as the dropdown rendered it. */
  value: string;
}

const CHIP_LABEL: Record<FilterKey, string> = {
  scope: "Scope",
  entity: "Entity",
  site: "Site",
  sites: "Sites",
  month: "Month",
  category: "Category",
  source: "Source type",
  status: "Evidence",
  quality: "Data quality",
  owner: "Owner",
  q: "Search",
};

/** A grade in the reader's words. One home: the dropdown, the chip and the Readiness link say it
 * the same way or a reader checks whether they are the same filter. */
export function gradeLabel(grade: string): string {
  return `Grade ${grade}`;
}

export function chipsFor(filters: Filters, options: Options): Chip[] {
  const chips: Chip[] = [];
  for (const key of FILTER_KEYS) {
    const raw = filters[key];
    if (!raw) continue;
    let value = raw.replace(/_/g, " ");
    if (key === "site") {
      value = options.sites.find(([id]) => id === raw)?.[1] ?? raw;
    } else if (key === "sites") {
      // A count, not the names: the banner above the register names them, and a chip that listed
      // forty sites would be wider than the screen.
      const count = siteIds(raw).length;
      value = `${count} site${count === 1 ? "" : "s"}`;
    } else if (key === "status") {
      value = STATUSES.find((status) => status.value === raw)?.label ?? raw;
    } else if (key === "scope") {
      value = `Scope ${raw}`;
    } else if (key === "quality") {
      // "Grade A", as the register's own rows and dropdown say it — never the bare letter, which
      // beside a control reading "Grade A" is the same filter described two ways.
      value = gradeLabel(raw);
    } else if (key === "owner") {
      // The **name**, not the UUID. A chip reading `01a1…` beside a dropdown reading a person's
      // name is the same filter described two ways, which is `month`'s lesson one branch down.
      value =
        raw === UNOWNED
          ? "Nobody"
          : (options.owners.find(([id]) => id === raw)?.[1] ?? raw);
    } else if (key === "month") {
      // Named, like the dropdown it came from. A chip reading `2025-04` beside a control reading
      // "April 2025" is the same filter described two ways, and a reader checks whether they are
      // the same filter rather than reading the figures.
      value = monthLabel(raw);
    }
    chips.push({ key, label: CHIP_LABEL[key], value });
  }
  return chips;
}

// ------------------------------------------------------------------- the filtered export (EXP-21)

/** What the exported file says about itself, above its own columns. */
export interface ExportContext {
  entity: string;
  period_start: string;
  period_end: string;
  method: string;
  chips: readonly Chip[];
  /** How many figures the window holds, against how many the file carries. */
  of: number;
}

const COLUMNS = [
  "scope",
  "entity",
  "site",
  "category",
  "source_type",
  "period_start",
  "period_end",
  "quantity",
  "unit",
  "kgco2e",
  "scope2_method",
  "data_quality",
  "evidence_chain",
  // **The owner, and why it is two columns.** `owner` is the name a reader recognises; `owner_basis`
  // is how it was reached, and without it "" is three different facts — nobody assigned to a
  // declared stream, no stream at all, and two streams that disagree. A filtered export whose empty
  // cells mean three things is one somebody draws the wrong conclusion from.
  //
  // Safe to add because this file is a **view** that cannot be sent back: the editable workbook is
  // the round trip, and `test_our_own_export_re_imports_as_entirely_unchanged` guards that one.
  "owner",
  "owner_basis",
  "receipt",
] as const;

function cell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The rows on screen as a CSV, with the filter context above the columns.
 *
 * **The context travels in the file, not only in the filename.** A file holding 12 of 340 figures
 * and saying nothing about why is the trap this requirement exists to close: it will be read as an
 * inventory, and a renamed download has lost the only statement there was. The cost is real and is
 * stated here rather than discovered — a `#` block is data to a parser not configured to skip it,
 * which is why the block is above the header and never between rows.
 *
 * **This is the view, not the editable workbook.** `FigureOut` carries no `line_id`, so a file
 * built from these rows cannot be matched back to the readings it came from and must not pretend
 * to be a round trip. The editable export is whole-period and lives on Bulk edit (XLS-01);
 * narrowing *that* needs the server's export to accept the same filters.
 *
 * Figures are written as the wire's own decimal strings — unrounded, in kilograms. Rule 5 puts
 * rounding in the presentation layer, and a spreadsheet somebody reconciles against a receipt is
 * not a place to lose digits.
 */
export function toCsv(
  rows: readonly Figure[],
  context: ExportContext,
  entities: ReadonlyMap<string, string>,
): string {
  const filters =
    context.chips.length === 0
      ? "none — every figure in the period"
      : context.chips.map((chip) => `${chip.label}: ${chip.value}`).join("; ");
  const lines = [
    "# EmitIQ — emissions inventory, as filtered on screen",
    `# entity: ${context.entity}`,
    `# period: ${context.period_start} to ${context.period_end}`,
    `# scope 2 basis: ${context.method}-based (never summed with the other)`,
    `# filters: ${filters}`,
    `# figures: ${rows.length} of ${context.of} in this period`,
    "# emissions are kgCO2e, unrounded, as calculated. Each row's receipt is at",
    "#   /emissions?receipt=<receipt>",
    COLUMNS.join(","),
  ];
  for (const figure of rows) {
    lines.push(
      [
        figure.scope,
        entities.get(figure.facility_id) ?? "",
        figure.facility_name,
        figure.category,
        figure.fuel_or_carrier,
        figure.period_start,
        figure.period_end,
        figure.quantity,
        figure.unit,
        figure.kgco2e,
        figure.method ?? "",
        figure.quality,
        figure.receipt_is_complete
          ? "complete"
          : `breaks at ${figure.evidence_broken_at ?? "an unknown step"}`,
        figure.owner_name ?? "",
        figure.owner_basis,
        figure.id,
      ]
        .map(cell)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

/** The download's name. Says the period and whether it is the whole of it. */
export function csvFilename(context: ExportContext, rows: readonly Figure[]): string {
  const whole = rows.length === context.of;
  return `emitiq-inventory-${context.period_start}-${context.period_end}${whole ? "" : "-filtered"}.csv`;
}

// -------------------------------------------------------------------------------- the hook

export interface Register {
  /** The filters in force - after anything unofferable has been dropped. */
  filters: Filters;
  /** The site ids the address asked for through `sites`, before the window was consulted: the
   * banner compares this with `filters.sites` to say how many of them have a figure to show. */
  askedSites: readonly string[];
  options: Options;
  chips: readonly Chip[];
  isFiltered: boolean;
  /** The figures read so far, in the server's order. **Not the selection**: `total` is how many the
   * selection holds, and anything that counts, sums or exports uses that and `readSelection`. */
  rows: readonly Figure[];
  /** The server's count of the whole selection. */
  total: number;
  /** The selection's whole-record sum, in kilograms, as the server states it. */
  wholeRecordKg: string | undefined;
  /** The subset the table renders, and how many of the selection it leaves out. See `RENDER_CAP`. */
  rendered: Rendered;
  /** The paged read behind `rows`, for a drawer that lists the same selection. */
  read: FigureCutRead;
  /** The window's own facets, for the overview's explore links. */
  facets: FigureFacets | undefined;
  /** The figures read, and the options read: `isPending` is the first page of either, `error` the
   * first failure. A register whose filters could not be offered is not an empty one. */
  isPending: boolean;
  error: unknown;
  retry: () => void;
  /** More exists on the server than the register will draw (`REGISTER_DRAW_LIMIT`). */
  ceilingReached: boolean;
  /** The filters' options could not be read, whichever tab is showing: the chips, the explore links and
   * the register's choices all stand on them. */
  optionsError: unknown;
  retryOptions: () => void;
  set: (key: FilterKey, value: string) => void;
  clear: (key: FilterKey) => void;
  clearAll: () => void;
  /** Draw at least this many rows, through the URL. See `ScreenSearch.rows`.
   *
   * Takes the ceiling rather than a delta, so the control's label and what it does are the same
   * number: "Show 500 more" at 500 asks for 1,000, and two presses racing each other land on 1,000
   * rather than 1,500. A delta read from state the router has not committed yet is how a double
   * press skips a batch.
   */
  expand: (to: number) => void;
  /** Every figure of the selection, read from the server now - what a file holds. */
  readSelection: () => Promise<Figure[]>;
  /** The entity of each site, for the file. */
  entities: ReadonlyMap<string, string>;
}

/** The most figures the register will draw. The selection is the server's and may hold far more; a
 * register with a hundred thousand rows in the document is not a register, so past this it says how
 * many exist and sends the reader to a filter. */
export const REGISTER_DRAW_LIMIT = 10_000;

export function useRegister({
  window,
  method,
  facilities,
  active,
  sitesKnown,
}: {
  window: Window | undefined;
  method: Scope2Method;
  facilities: readonly FacilityRow[];
  /** Whether the register is what is on screen (its tab): only then are figures read. The options
   * are read regardless, because the chips above every tab name the filters in force. */
  active: boolean;
  /** Whether the site list has answered: the entity filter resolves through it, so a register read
   * before it would be read twice - once without the entity and once with. */
  sitesKnown: boolean;
}): Register {
  const enabled = active && sitesKnown;
  // `rows` as well as the filters: the ceiling is URL state too, for EXP-12's reason, and reading
  // it here rather than in the screen keeps the one place that knows how many rows are drawn.
  const search: FilterSearch & { rows?: number } = useSearch({ strict: false });
  const navigate = useNavigate();

  const entities = useMemo(() => entityIndex(facilities), [facilities]);
  const asked = useMemo(() => parseFilters(search), [search]);
  const askedScope = asked.scope === "1" || asked.scope === "2" || asked.scope === "3" ? asked.scope : undefined;
  const windowFacets = useFigureFacets(window, method);
  // The same cache entry as the window's when no scope is asked for, so it costs a request only when
  // a scope is chosen.
  const scopedFacets = useFigureFacets(window, method, askedScope);
  const options = useMemo(
    () => optionsFor({ window: windowFacets.data, scoped: scopedFacets.data }, asked, entities),
    [windowFacets.data, scopedFacets.data, asked, entities],
  );
  const filters = useMemo(() => effective(asked, options), [asked, options]);
  const cut = useMemo(() => cutOfRegister(filters, entities), [filters, entities]);
  const cap = ceiling(search.rows);

  // **Not before the options are known.** `effective` drops a filter the screen could not have
  // offered, so asking before the facets arrive would read the whole window and then read it again
  // narrowed - a flash of the wrong register and a request wasted.
  const optionsKnown = windowFacets.data !== undefined && scopedFacets.data !== undefined;
  const read: FigureCutRead = useFiguresPaged(
    enabled && optionsKnown ? window : undefined,
    method,
    cut,
    { keepPrevious: true, ceiling: REGISTER_DRAW_LIMIT },
  );
  const rows = read.figures;

  // The register draws `cap` rows and the server pages by `FIGURES_PAGE`, so reaching the ceiling the
  // reader asked for is reading pages until enough are here. One page at a time: the effect runs again
  // when each lands.
  const more = useRef(read.loadMore);
  useEffect(() => {
    more.current = read.loadMore;
  });
  const want = Math.min(cap, read.total);
  useEffect(() => {
    if (!enabled) return;
    if (read.canLoadMore && !read.loadingMore && read.moreError === null && read.figures.length < want) {
      more.current();
    }
  }, [enabled, read.canLoadMore, read.loadingMore, read.moreError, read.figures.length, want]);

  const rendered = useMemo(() => render(rows, cap, read.total), [rows, cap, read.total]);
  const chips = useMemo(() => chipsFor(filters, options), [filters, options]);

  const patch = useCallback(
    (next: FilterSearch & { rows?: number }) => {
      // `replace: true`, like every other patch in `useScreen`. The search box writes a parameter
      // per keystroke, so pushing history would make the Back button walk letter by letter out of
      // a filter instead of leaving the screen. Returning to a filtered list is handled the other
      // way round: opening a record is a push *from* the filtered URL, so Back comes back to it.
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, ...next }),
        replace: true,
      });
    },
    [navigate],
  );

  const askedSites = useMemo(() => siteIds(asked.sites), [asked]);
  const failure = windowFacets.error ?? scopedFacets.error ?? read.error;

  return {
    filters,
    askedSites,
    options,
    chips,
    isFiltered: isFiltered(filters),
    rows,
    read,
    facets: windowFacets.data,
    total: read.total,
    wholeRecordKg: read.wholeRecordKg,
    rendered,
    // **Pending until the figures have answered**, and not "zero figures" in the meantime: a register
    // that states a count before its first page has arrived has told the reader there is nothing here.
    isPending: active && (!sitesKnown || !optionsKnown || read.isPending) && !failure,
    error: failure,
    retry: () => {
      if (windowFacets.error) void windowFacets.refetch();
      if (scopedFacets.error) void scopedFacets.refetch();
      if (read.error) read.refetch();
    },
    ceilingReached: read.ceilingReached,
    optionsError: windowFacets.error ?? scopedFacets.error,
    retryOptions: () => {
      if (windowFacets.error) void windowFacets.refetch();
      if (scopedFacets.error) void scopedFacets.refetch();
    },
    set: (key, value) => patch(justOne(key, value === "" ? undefined : value)),
    clear: (key) => patch(justOne(key, undefined)),
    // Written out rather than `toSearch(NO_FILTERS)` so the URL loses the keys instead of
    // carrying eight empty ones - a cleared filter should leave a link somebody can read.
    //
    // **`rows` is deliberately not in it.** Clearing a filter is not un-expanding a list: a reader
    // who asked for every row and then removed a site filter asked a wider question, not a
    // shorter answer, and a register that silently went back to 500 would have answered a
    // question they did not ask.
    clearAll: () => patch(toSearch(NO_FILTERS)),
    // `undefined` at the opening cap, so a register nobody expanded leaves no trace in a shared
    // link - the same rule as a cleared filter, and the reason `ceiling` treats absent and 500 as
    // one thing.
    expand: (to) => patch({ rows: to <= RENDER_CAP ? undefined : to }),
    readSelection: () => (window ? readEveryFigure(window, method, cut) : Promise.resolve([])),
    entities,
  };
}
